import { AxiosRequestConfig, AxiosResponse } from 'axios';

import { ApiResponse, BaseHttpClient, HttpClientConfig, RequestOptions } from './BaseHttpClient';

export interface ClientRequestOptions extends RequestOptions {
  showLoader?: boolean;
  loaderMessage?: string;
}

export type LoadingCallback = (loading: boolean, message?: string) => void;

/**
 * Client-side API Service
 *
 * SECURITY: This service MUST ONLY point to API proxy routes (/api/*)
 * Never configure this to point directly to backend URL as it would expose
 * the backend API and bypass security controls.
 *
 * Extends BaseHttpClient with client-specific features:
 * - Loading state management
 * - Browser-specific optimizations
 * - Client-side caching integration
 *
 * All requests from this service go through:
 * Client → ClientApiService (/api/*) → API Proxy → SecureApiService → Backend
 */
export class ClientApiService extends BaseHttpClient {
  private loadingCallback?: LoadingCallback;

  constructor(config?: Partial<HttpClientConfig>) {
    const defaultConfig: HttpClientConfig = {
      // SECURITY: Client-side requests must go through Next.js API routes.
      // Use proxy route if available (e.g. /api/proxy), otherwise fall back to /api.
      baseURL: process.env.NEXT_PUBLIC_API_PROXY_URL || process.env.NEXT_PUBLIC_API_URL || '/api',
      timeout: 30000,
      enableLogging: true,
      enableInterceptors: true,
      ...config,
    };

    super(defaultConfig);
  }

  /**
   * Set loading callback for global loading state management
   */
  public setLoadingCallback(callback: LoadingCallback): void {
    this.loadingCallback = callback;
  }

  /**
   * Clear loading callback
   */
  public clearLoadingCallback(): void {
    this.loadingCallback = undefined;
  }

  /**
   * Trigger loading state
   */
  protected triggerLoading(loading: boolean, message?: string): void {
    if (this.loadingCallback) {
      this.loadingCallback(loading, message);
    }
  }

  /**
   * GET request with loading state
   */
  public async get<T>(url: string, options?: ClientRequestOptions): Promise<ApiResponse<T>> {
    const { showLoader = true, loaderMessage, ...baseOptions } = options || {};

    try {
      if (showLoader) {
        this.triggerLoading(true, loaderMessage);
      }

      return await super.get<T>(url, baseOptions);
    } finally {
      if (showLoader) {
        this.triggerLoading(false);
      }
    }
  }

  /**
   * POST request with loading state
   */
  public async post<T>(url: string, data?: any, options?: ClientRequestOptions): Promise<ApiResponse<T>> {
    const { showLoader = true, loaderMessage, ...baseOptions } = options || {};

    try {
      if (showLoader) {
        this.triggerLoading(true, loaderMessage);
      }

      return await super.post<T>(url, data, baseOptions);
    } finally {
      if (showLoader) {
        this.triggerLoading(false);
      }
    }
  }

  /**
   * PUT request with loading state
   */
  public async put<T>(url: string, data?: any, options?: ClientRequestOptions): Promise<ApiResponse<T>> {
    const { showLoader = true, loaderMessage, ...baseOptions } = options || {};

    try {
      if (showLoader) {
        this.triggerLoading(true, loaderMessage);
      }

      return await super.put<T>(url, data, baseOptions);
    } finally {
      if (showLoader) {
        this.triggerLoading(false);
      }
    }
  }

  /**
   * PATCH request with loading state
   */
  public async patch<T>(url: string, data?: any, options?: ClientRequestOptions): Promise<ApiResponse<T>> {
    const { showLoader = true, loaderMessage, ...baseOptions } = options || {};

    try {
      if (showLoader) {
        this.triggerLoading(true, loaderMessage);
      }

      return await super.patch<T>(url, data, baseOptions);
    } finally {
      if (showLoader) {
        this.triggerLoading(false);
      }
    }
  }

  /**
   * DELETE request with loading state
   */
  public async delete<T>(url: string, options?: ClientRequestOptions): Promise<ApiResponse<T>> {
    const { showLoader = true, loaderMessage, ...baseOptions } = options || {};

    try {
      if (showLoader) {
        this.triggerLoading(true, loaderMessage);
      }

      return await super.delete<T>(url, baseOptions);
    } finally {
      if (showLoader) {
        this.triggerLoading(false);
      }
    }
  }

  /**
   * Generic request with loading state
   */
  public async request<T>(config: ClientRequestOptions & AxiosRequestConfig): Promise<ApiResponse<T>> {
    const { showLoader = true, loaderMessage, ...baseConfig } = config;

    try {
      if (showLoader) {
        this.triggerLoading(true, loaderMessage);
      }

      return await super.request<T>(baseConfig);
    } finally {
      if (showLoader) {
        this.triggerLoading(false);
      }
    }
  }

  /**
   * API request wrapper that returns data directly (for backward compatibility)
   */
  public async apiRequest<T>(config: ClientRequestOptions & AxiosRequestConfig): Promise<T> {
    const response = await this.request<T>(config);
    // with the new shape, payload is nested under response.data
    return response.data as T;
  }
}
